import { useEffect, useMemo, useState } from 'react'
import { distanceFrom, mockClubs, type MockClub } from '@/shared/mock/data'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { useDirectoryPlaces, useAllPlaces, mockToDirectoryClub, type DirectoryClub } from './use-places'

// Lugares feed — owns the 4 states + chip filter + free-text search. Under
// real auth the list is the `places_courts` directory (projected from
// clubmanager, queried by cityKey); under the dev bypass the certified mock
// pipeline stays byte-identical so design walks keep their fixtures.
export type FeedStatus = 'loading' | 'loaded' | 'empty' | 'error'

export const LUGARES_FILTERS = ['Cerca de mí', 'Indoor', 'Outdoor', 'Con clases'] as const

function applyFilter(list: DirectoryClub[], filter: string): DirectoryClub[] {
  if (filter === 'Indoor') return list.filter((c) => c.indoor)
  if (filter === 'Outdoor') return list.filter((c) => !c.indoor)
  if (filter === 'Cerca de mí')
    // Unknown distances sort last, never hidden — missing data must not
    // disappear a real club.
    return [...list].sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity))
  // 'Con clases' is a stub facet (no field yet) — kept selectable, returns none
  // to exercise the empty state honestly rather than faking matches.
  if (filter === 'Con clases') return []
  return list
}

function applyQuery(list: DirectoryClub[], query: string): DirectoryClub[] {
  const q = query.trim().toLowerCase()
  if (!q) return list
  return list.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      c.city.toLowerCase().includes(q) ||
      c.address.toLowerCase().includes(q),
  )
}

/** Radius applies only to clubs with a known distance — never hides unknowns. */
function applyRadius(list: DirectoryClub[], radiusKm: number): DirectoryClub[] {
  return list.filter((c) => c.distanceKm == null || c.distanceKm <= radiusKm)
}

// DEV-only: `?estado=error` forces the error state so the 4-states contract is
// verifiable in walks/reviews (the mock never fails on its own).
function forcedError(): boolean {
  return new URLSearchParams(window.location.search).get('estado') === 'error'
}

// Facet/location survive push/pop navigation (same rule as the Partidos feed).
const lugaresMemory = { filter: 'Cerca de mí', location: 'Granada', radiusKm: 10 }

export function useLugares() {
  const [base, setBase] = useState<FeedStatus>('loading')
  const [filter, setFilterState] = useState<string>(lugaresMemory.filter)
  const [query, setQuery] = useState('')
  const [location, setLocationState] = useState(lugaresMemory.location)
  const [radiusKm, setRadiusKmState] = useState(lugaresMemory.radiusKm)
  const directory = useDirectoryPlaces(location)
  // A typed search means "find this club, wherever it is" — the zone picker
  // and the search box answer different questions, and scoping the second to
  // the first's selection is what made a real club produce "ningún club
  // coincide" for a query that matched it perfectly, just in another zone.
  // `place-picker.tsx` (the Crear-partido club picker) already searches
  // everywhere; this brings Lugares' own search in line with it. Always
  // called (rules of hooks) — `enabled` inside `useAllPlaces` isn't needed
  // here since the query is cheap and cached (`staleTime` in use-places.ts).
  const searching = query.trim().length > 0
  const allPlaces = useAllPlaces()

  const setFilter = (v: string) => {
    lugaresMemory.filter = v
    setFilterState(v)
  }
  const setLocation = (v: string) => {
    lugaresMemory.location = v
    setLocationState(v)
  }
  const setRadiusKm = (v: number) => {
    lugaresMemory.radiusKm = v
    setRadiusKmState(v)
  }

  // Mock branch keeps its brief skeleton delay (design fixture).
  useEffect(() => {
    if (!AUTH_BYPASS) return
    const t = setTimeout(() => setBase(forcedError() ? 'error' : 'loaded'), 450)
    return () => clearTimeout(t)
  }, [])

  const clubs = useMemo(() => {
    if (AUTH_BYPASS) {
      if (base !== 'loaded') return []
      // Location is a real axis (see use-partidos): recompute distances from
      // the selected centre before radius + facet filters.
      const centred: DirectoryClub[] = mockClubs.map((c: MockClub) => ({
        ...mockToDirectoryClub(c),
        distanceKm: distanceFrom(location, c.distanceKm ?? 0),
      }))
      return applyRadius(applyQuery(applyFilter(centred, filter), query), radiusKm)
    }
    const list = searching ? (allPlaces.data ?? []) : (directory.data ?? [])
    return applyRadius(applyQuery(applyFilter(list, filter), query), radiusKm)
  }, [base, location, filter, query, radiusKm, searching, directory.data, allPlaces.data])

  // While searching, the province-wide read is the one that must have
  // answered — the zone-scoped one may still be loading (or may never even
  // be used this render) and must not gate the search result on it.
  const active = searching ? allPlaces : directory
  const status: FeedStatus = AUTH_BYPASS
    ? base === 'loaded' && clubs.length === 0
      ? 'empty'
      : base
    : forcedError() || active.isError
      ? 'error'
      : active.isPending
        ? 'loading'
        : clubs.length === 0
          ? 'empty'
          : 'loaded'

  function refresh() {
    if (AUTH_BYPASS) {
      setBase('loading')
      setTimeout(() => setBase(forcedError() ? 'error' : 'loaded'), 450)
      return
    }
    void (searching ? allPlaces.refetch() : directory.refetch())
  }

  return {
    status,
    clubs,
    filter,
    setFilter,
    query,
    setQuery,
    location,
    setLocation,
    radiusKm,
    setRadiusKm,
    refresh,
    retry: refresh,
  }
}
